/**
 * Catalog mega menu: single `data-active-branch` on .mega-menu__l1-panels (replaces :has() CSS),
 * outside-click + ESC to close, keyboard navigation on the L1 rail, optional lazy panel hydration.
 */
class CatalogMegaMenu extends HTMLElement {
  getBranchItems() {
    return Array.from(this.querySelectorAll('.mega-menu__l1-rail-item--branch .mega-menu__l1-item'));
  }

  getBranchListItems() {
    return Array.from(this.querySelectorAll('.mega-menu__l1-rail-item--branch'));
  }

  /** @param {number} branchIndex 1-based (matches L1 panel nth-child) */
  hydrateBranchPanel(branchIndex) {
    if (this.dataset.lazyPanels !== 'true' || !Number.isFinite(branchIndex) || branchIndex < 1) return;
    const panel = this.panelsEl.querySelector(`.mega-menu__l1-panel:nth-child(${branchIndex})`);
    if (!panel) return;
    const tmpl = panel.querySelector('template.mega-menu__l1-panel-template');
    if (tmpl && !panel.hasAttribute('data-mega-hydrated')) {
      panel.appendChild(tmpl.content.cloneNode(true));
      panel.setAttribute('data-mega-hydrated', 'true');
      tmpl.remove();
    }
  }

  connectedCallback() {
    this.details = this.querySelector('details#Details-HeaderMenu-Single') || this.querySelector('details.mega-menu--single');
    this.panelsEl = this.querySelector('.mega-menu__l1-panels');

    if (!this.details || !this.panelsEl) {
      this._menuInit = false;
      return;
    }
    this._menuInit = true;

    this.boundOnDetailsToggle = this.onDetailsToggle.bind(this);
    this.boundOnPointerDown = this.onDocumentPointerDown.bind(this);
    this.boundOnKeyUp = this.onKeyUp.bind(this);
    this.boundOnKeyDownRail = this.onKeyDownRail.bind(this);
    this.boundOnPanelArrowLeft = this.onPanelArrowLeft.bind(this);

    this.details.addEventListener('toggle', this.boundOnDetailsToggle);
    document.addEventListener('pointerdown', this.boundOnPointerDown, true);
    this.addEventListener('keyup', this.boundOnKeyUp);
    this.addEventListener('keydown', this.boundOnPanelArrowLeft, true);

    this.getBranchItems().forEach((btn) => {
      const li = btn.closest('.mega-menu__l1-rail-item--branch');
      const index = li ? parseInt(li.getAttribute('data-mega-branch-index') || '0', 10) : 0;
      btn.addEventListener('mouseenter', () => {
        if (this.details.hasAttribute('open')) this.setActiveBranch(index);
      });
      btn.addEventListener('focus', () => {
        if (this.details.hasAttribute('open')) this.setActiveBranch(index);
      });
      // L1 branch is now a real link: hover/focus opens its panel, click navigates to the parent URL.
      btn.addEventListener('keydown', this.boundOnKeyDownRail);
    });
  }

  disconnectedCallback() {
    if (!this._menuInit) return;
    document.removeEventListener('pointerdown', this.boundOnPointerDown, true);
    this.details.removeEventListener('toggle', this.boundOnDetailsToggle);
    this.removeEventListener('keyup', this.boundOnKeyUp);
    this.removeEventListener('keydown', this.boundOnPanelArrowLeft, true);
  }

  onDetailsToggle() {
    if (!this.panelsEl) return;
    if (this.details.open) {
      const start = this.getDefaultActiveBranchIndex();
      this.setActiveBranch(start);
    } else {
      this.panelsEl.removeAttribute('data-active-branch');
      this.getBranchItems().forEach((btn) => {
        btn.setAttribute('aria-expanded', 'false');
      });
    }
  }

  getDefaultActiveBranchIndex() {
    const items = this.getBranchListItems();
    for (const li of items) {
      const activeBtn = li.querySelector('.mega-menu__l1-item--active');
      if (activeBtn) {
        const n = parseInt(li.getAttribute('data-mega-branch-index') || '0', 10);
        if (n > 0) return n;
      }
    }
    const first = items[0];
    if (first) {
      return parseInt(first.getAttribute('data-mega-branch-index') || '1', 10);
    }
    return 1;
  }

  setActiveBranch(branchIndex) {
    if (!this.panelsEl) return;
    this.panelsEl.setAttribute('data-active-branch', String(branchIndex));
    this.hydrateBranchPanel(branchIndex);

    this.getBranchListItems().forEach((li) => {
      const idx = parseInt(li.getAttribute('data-mega-branch-index') || '0', 10);
      const btn = li.querySelector('.mega-menu__l1-item');
      if (!btn) return;
      const isActive = idx === branchIndex;
      btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }

  getActiveBranchIndex() {
    const v = this.panelsEl.getAttribute('data-active-branch');
    return v ? parseInt(v, 10) : 0;
  }

  onPanelArrowLeft(event) {
    if (event.key !== 'ArrowLeft' || !event.target || typeof event.target.closest !== 'function') return;
    if (!event.target.closest('.mega-menu__l1-panel')) return;
    if (!this.details || !this.details.hasAttribute('open')) return;
    const activeIndex = this.getActiveBranchIndex();
    const btn = this.querySelector(
      `.mega-menu__l1-rail-item--branch[data-mega-branch-index="${activeIndex}"] .mega-menu__l1-item`
    );
    if (btn) {
      event.preventDefault();
      btn.focus();
    }
  }

  onKeyDownRail(event) {
    if (!this.details || !this.details.hasAttribute('open')) return;
    const btn = event.currentTarget;
    const li = btn.closest('.mega-menu__l1-rail-item--branch');
    const current = parseInt(li.getAttribute('data-mega-branch-index') || '0', 10);
    const listItems = this.getBranchListItems();
    const i = listItems.findIndex((el) => el.querySelector('.mega-menu__l1-item') === btn);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = listItems[i + 1];
      if (next) {
        const nextBtn = next.querySelector('.mega-menu__l1-item');
        if (nextBtn) nextBtn.focus();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prev = listItems[i - 1];
      if (prev) {
        const prevBtn = prev.querySelector('.mega-menu__l1-item');
        if (prevBtn) prevBtn.focus();
      }
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.hydrateBranchPanel(current);
      this.setActiveBranch(current);
      const panel = this.panelsEl?.querySelector(`.mega-menu__l1-panel:nth-child(${current})`);
      if (panel) {
        const focusable = panel.querySelector('a[href], button:not([disabled])');
        if (focusable) {
          requestAnimationFrame(() => focusable.focus());
        }
      }
    }
  }

  onKeyUp(event) {
    if (event.code !== 'Escape') return;
    if (!this.details || !this.details.hasAttribute('open')) return;
    this.details.removeAttribute('open');
    const sum = this.details.querySelector('summary');
    if (sum) {
      sum.focus();
    }
  }

  onDocumentPointerDown(event) {
    if (!this.details || !this.details.hasAttribute('open')) return;
    if (this.contains(event.target)) return;
    this.details.removeAttribute('open');
  }
}

if (!customElements.get('catalog-mega-menu')) {
  customElements.define('catalog-mega-menu', CatalogMegaMenu);
}
